<template>
  <div
    ref="nodeEl"
    class="node"
    :class="{
      root: root,
      'no-children': children.length === 0,
      'simple-children': simpleChildren,
      indirect: node.indirect,
    }"
  >
    <div class="toprow">
      <svg v-if="children.length || spouse" class="limbs" version="1.1">
        <line v-for="line in limbLines" :key="line.x1 + line.x2 + line.y1 + line.y2" v-bind="line" />
      </svg>
      <CharacterCard :data="node" class="person" />
      <CharacterCard v-if="spouse" :data="spouse" class="spouse" />
    </div>

    <div v-if="children.length" class="children">
      <FamilyTreeNode v-for="child in children" :key="child.name" :tree="tree" :node="child" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { treeGet, type FamilyTree, type CharacterData } from './familytree'
import CharacterCard from './CharacterCard.vue'

const props = defineProps<{
  tree: FamilyTree
  node: CharacterData
  root?: boolean
}>()

const children = computed(() => props.node.children?.map((name) => treeGet(props.tree, name)).filter((p) => !!p) ?? [])
const spouse = computed(() => treeGet(props.tree, props.node.spouse))
const simpleChildren = computed(() => children.value.length === 1 && !children.value[0].spouse && !props.node.spouse)

const nodeEl = ref(null as HTMLElement | null)
const frame = computed(() => nodeEl.value?.getBoundingClientRect())

type Point = { x: number; y: number }
type Line = { x1: number; y1: number; x2: number; y2: number }
type Position = 'top' | 'left' | 'right' | 'bottom'

const toFrame = ({ x, y }: Point): Point => ({ x: x - (frame.value?.x || 0), y: y - (frame.value?.y || 0) })

const getPoint = (el: Element, position: Position): Point => {
  // get coords in the node's frame of reference
  const rect = el.getBoundingClientRect()

  if (position === 'top') {
    return toFrame({ x: rect.left + rect.width / 2, y: rect.top })
  } else if (position === 'bottom') {
    return toFrame({ x: rect.left + rect.width / 2, y: rect.bottom })
  } else if (position === 'left') {
    return toFrame({ x: rect.left, y: rect.top + rect.height / 2 })
  } else if (position === 'right') {
    return toFrame({ x: rect.right, y: rect.top + rect.height / 2 })
  }
  return { x: 0, y: 0 }
}

const lineBetween = (p1: Point, p2: Point): Line => ({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y })

const lerp = (l: Line, f: number): Point => ({ x: l.x1 + (l.x2 - l.x1) * f, y: l.y1 + (l.y2 - l.y1) * f })

const limbLines = computed<Line[]>(() => {
  const person = nodeEl.value?.querySelector(':scope > .toprow .person')
  if (!person) return [] // still rendering
  const spouse = nodeEl.value?.querySelector(':scope > .toprow .spouse')
  const children = [...(nodeEl.value?.querySelectorAll(':scope > .children > .node > .toprow .person') ?? [])]

  const res = []

  let origin = getPoint(person, 'bottom')

  if (spouse) {
    const p1 = getPoint(person, 'right')
    const p2 = { x: p1.x + 20, y: p1.y }
    const marriage = lineBetween(p1, p2)
    res.push(marriage)
    origin = lerp(marriage, 0.5)
  }

  if (simpleChildren.value) {
    res.push(lineBetween(origin, getPoint(children[0], 'top')))
  } else if (children.length) {
    const baselineY = Math.max(getPoint(person, 'bottom').y, spouse ? getPoint(spouse, 'bottom').y : 0) + 16
    const baseline = { x: origin.x, y: baselineY }

    let baselineMin = baseline.x
    let baselineMax = baseline.x
    res.push(lineBetween(origin, baseline))

    const leeway = 20 // if the baseline segment is only 20px to the side, just draw a straight line
    children.forEach((child) => {
      const end = getPoint(child, 'top')
      const angle = { x: end.x, y: baselineY }
      if (Math.abs(baseline.x - end.x) <= leeway) {
        angle.x = baseline.x
        end.x = baseline.x
      }
      res.push(lineBetween(angle, end))
      baselineMin = Math.min(baselineMin, end.x)
      baselineMax = Math.max(baselineMax, end.x)
    })
    res.push(lineBetween({ x: baselineMin, y: baselineY }, { x: baselineMax, y: baselineY }))
  }

  //console.log(JSON.stringify(res, null, 2))

  return res
})
</script>

<style scoped lang="postcss">
.node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .limbs {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;

    line {
      stroke: var(--primary);
      stroke-width: 2px;
    }
  }

  &.indirect {
    > .toprow .limbs line {
      stroke-dasharray: 4;
    }
  }

  .toprow {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 20px;
    z-index: 3;
    padding-bottom: 2em;
  }

  &.simple-children > .toprow {
    padding-bottom: 1em;
  }

  &.no-children > .toprow {
    padding-bottom: 0;
  }

  .children {
    display: grid;
    gap: 1em;
    grid-auto-flow: column;
  }
}
</style>
